<script lang="ts" setup>
import {nextTick, onMounted, onUnmounted, ref, watch} from 'vue'
import type {ParsedRow, ImportResponse} from '@/types/bulk-user-import'

const props = defineProps<{
  rows: ParsedRow[]
  busy: boolean
  result: ImportResponse | null
}>()

// const emit = defineEmits<{
//   remove: [index: number]
//   save: [index: number, values: EditValues]
//   add: []
// }>()
const emit = defineEmits(['remove', 'save', 'add']) // typed version of defineEmits doesn't work in 2.7

interface EditValues {
  email: string
  fullName: string
  type: string
  gradeLevel: string
  schoolId: string
}

interface EditErrors {
  email?: string
  fullName?: string
  type?: string
  gradeLevel?: string
  schoolId?: string
}

const userTypeLabels: Record<string, string> = {
  teacher: 'Teacher',
  homeschooler: 'Homeschooler',
  school_admin: 'School Admin',
  district_admin: 'District Admin',
  contact: 'Contact',
}

const userTypes = ['teacher', 'homeschooler', 'school_admin', 'district_admin', 'contact']

const editingIndex = ref<number | null>(null)
const editValues = ref<EditValues>({ email: '', fullName: '', type: 'teacher', gradeLevel: '', schoolId: '' })
const editErrors = ref<EditErrors>({})
const editRowRef = ref<HTMLElement | null>(null)

function setEditRowRef(el: Element | null) {
  editRowRef.value = el as HTMLElement | null
}

// When rows update and a row at index 0 appears fresh (no email), auto-enter edit mode
watch(() => props.rows, (newRows) => {
  if (editingIndex.value !== null) return
  const newRow = newRows.find(r => r.index === 0 && !r.email)
  if (newRow) {
    nextTick(() => startEdit(newRow))
  }
}, { immediate: true })

function errorFor(row: ParsedRow, field: string): string | undefined {
  if (row.index === editingIndex.value) {
    return editErrors.value[field as keyof EditErrors] || undefined
  }
  return row.errors.find(e => e.field === field)?.message
}

function startEdit(row: ParsedRow) {
  editingIndex.value = row.index
  editValues.value = {
    email: row.email,
    fullName: `${row.firstName} ${row.lastName}`.trim(),
    type: row.type,
    gradeLevel: row.gradeLevel || '',
    schoolId: row.schoolId ? String(row.schoolId) : '',
  }
  editErrors.value = {}
  nextTick(() => {
    // Focus the email input
    const input = editRowRef.value?.querySelector('input') as HTMLInputElement | null
    input?.focus()
  })
}

function validateRow(): boolean {
  const v = editValues.value
  const errs: EditErrors = {}

  if (!v.email.trim()) {
    errs.email = 'Email is required'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) {
    errs.email = 'Invalid email'
  }

  const name = v.fullName.trim()
  if (!name) {
    errs.fullName = 'Name is required'
  } else if (!name.includes(' ')) {
    errs.fullName = 'Enter first and last name'
  }

  if (!v.type) {
    errs.type = 'Type is required'
  }

  if (v.gradeLevel.trim() && !/^[1-6KMU]$/i.test(v.gradeLevel.trim())) {
    errs.gradeLevel = 'Invalid grade (1-6, K, M, U)'
  }

  if (v.schoolId.trim() && !/^\d+$/.test(v.schoolId.trim())) {
    errs.schoolId = 'Must be a numeric ID'
  }

  editErrors.value = errs
  return Object.keys(errs).length === 0
}

function saveEdit() {
  if (!validateRow()) return
  const idx = editingIndex.value
  if (idx === null) return

  const v = editValues.value
  const nameParts = v.fullName.trim().split(' ')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  emit('save', idx, {
    email: v.email.trim(),
    fullName: `${firstName} ${lastName}`,
    type: v.type,
    gradeLevel: v.gradeLevel.trim(),
    schoolId: v.schoolId.trim(),
  })
  editingIndex.value = null
}

function cancelEdit() {
  editingIndex.value = null
  editErrors.value = {}
}

defineExpose({ cancelEdit })

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    saveEdit()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    cancelEdit()
  }
}

// Click outside the edit row → try to save
function handleClickOutside(event: MouseEvent) {
  if (editingIndex.value === null) return
  const row = editRowRef.value
  if (row && !row.contains(event.target as Node)) {
    saveEdit()
  }
}

watch(editingIndex, (val) => {
  if (val !== null) {
    document.addEventListener('mousedown', handleClickOutside)
  } else {
    document.removeEventListener('mousedown', handleClickOutside)
  }
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>

<template>
  <div class="bulk-preview">
    <!-- Edit help banner -->
    <div v-if="editingIndex !== null" class="alert alert-info py-2 small mb-2">
      <i class="fas fa-pencil-alt"></i> Editing row {{ editingIndex + 1 }} ·
      <strong>Enter</strong> or <i class="fas fa-check"></i> to save ·
      <strong>Esc</strong> or <i class="fas fa-times"></i> to cancel ·
      Click outside the row to save
    </div>

    <!-- Users Table -->
    <div v-if="!result">
      <h5 v-if="rows.length > 0">
        Users — {{ rows.length }} row<span v-if="rows.length !== 1">s</span>
        <span v-if="rows.some(r => r.errors.length > 0 && r.index !== editingIndex)" class="text-danger">
          , {{ rows.filter(r => r.errors.length > 0 && r.index !== editingIndex).length }} with errors
        </span>
      </h5>

      <div class="table-responsive">
        <table class="table table-sm table-striped table-bordered">
          <thead>
            <tr>
              <th style="width:2em"></th>
              <th>#</th>
              <th>Email</th>
              <th>Name</th>
              <th>Type</th>
              <th>Grade</th>
              <th>School ID</th>
              <th>Status</th>
              <th style="width:2em"></th>
            </tr>
          </thead>
          <tbody>
            <!-- + New row -->
            <tr
              class="table-success"
              role="button"
              style="cursor:pointer"
              @click="editingIndex === null && emit('add')"
            >
              <td>
                <button
                  class="btn btn-sm btn-outline-success border-0"
                  title="Add new row"
                  tabindex="-1"
                  :disabled="editingIndex !== null"
                ><i class="fas fa-plus"></i></button>
              </td>
              <td colspan="8" class="text-muted">Click here to add a new row</td>
            </tr>
            <!-- Data rows -->
            <tr
              v-for="row in rows"
              :key="row.index"
              :class="{ 'table-danger': row.errors.length > 0 && row.index !== editingIndex }"
              :ref="row.index === editingIndex ? setEditRowRef : undefined"
              @keydown="row.index === editingIndex && handleKeydown($event)"
            >
              <!-- Edit / Save / Cancel button -->
              <td>
                <template v-if="row.index === editingIndex">
                  <button
                    class="btn btn-sm btn-outline-success border-0 mr-1"
                    title="Save (Enter)"
                    @click="saveEdit"
                  ><i class="fas fa-check"></i></button>
                  <button
                    class="btn btn-sm btn-outline-secondary border-0"
                    title="Cancel (Esc)"
                    @click="cancelEdit"
                  ><i class="fas fa-times"></i></button>
                </template>
                <button
                  v-else
                  class="btn btn-sm btn-outline-primary border-0"
                  title="Edit this row"
                  @click="startEdit(row)"
                ><i class="fas fa-pencil-alt"></i></button>
              </td>

              <td>{{ row.index + 1 }}</td>

              <!-- Email -->
              <td>
                <template v-if="row.index === editingIndex">
                  <input
                    v-model="editValues.email"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': editErrors.email }"
                    aria-label="Email"
                    style="min-width:10em"
                  />
                  <div v-if="editErrors.email" class="invalid-feedback d-block">{{ editErrors.email }}</div>
                </template>
                <template v-else>
                  {{ row.email }}
                  <div v-if="errorFor(row, 'email')" class="text-danger small">{{ errorFor(row, 'email') }}</div>
                </template>
              </td>

              <!-- Name -->
              <td>
                <template v-if="row.index === editingIndex">
                  <input
                    v-model="editValues.fullName"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': editErrors.fullName }"
                    placeholder="First Last"
                  />
                  <div v-if="editErrors.fullName" class="invalid-feedback d-block">{{ editErrors.fullName }}</div>
                </template>
                <template v-else>{{ row.firstName }} {{ row.lastName }}</template>
              </td>

              <!-- Type -->
              <td>
                <template v-if="row.index === editingIndex">
                  <select
                    v-model="editValues.type"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': editErrors.type }"
                  >
                    <option
                      v-for="t in userTypes"
                      :key="t"
                      :value="t"
                    >{{ userTypeLabels[t] }}</option>
                  </select>
                  <div v-if="editErrors.type" class="invalid-feedback d-block">{{ editErrors.type }}</div>
                </template>
                <template v-else>
                  {{ userTypeLabels[row.type] || row.type }}
                  <div v-if="errorFor(row, 'type')" class="text-danger small">{{ errorFor(row, 'type') }}</div>
                </template>
              </td>

              <!-- Grade -->
              <td>
                <template v-if="row.index === editingIndex">
                  <input
                    v-model="editValues.gradeLevel"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': editErrors.gradeLevel }"
                    aria-label="Grade"
                    style="width:4em"
                  />
                  <div v-if="editErrors.gradeLevel" class="invalid-feedback d-block">{{ editErrors.gradeLevel }}</div>
                </template>
                <template v-else>
                  {{ row.gradeLevel || '—' }}
                  <div v-if="errorFor(row, 'gradeLevel')" class="text-danger small">{{ errorFor(row, 'gradeLevel') }}</div>
                </template>
              </td>

              <!-- School ID -->
              <td>
                <template v-if="row.index === editingIndex">
                  <input
                    v-model="editValues.schoolId"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': editErrors.schoolId }"
                    aria-label="School ID"
                    style="width:5em"
                    inputmode="numeric"
                  />
                  <div v-if="editErrors.schoolId" class="invalid-feedback d-block">{{ editErrors.schoolId }}</div>
                </template>
                <template v-else>
                  {{ row.schoolId || '—' }}
                  <div v-if="errorFor(row, 'schoolId')" class="text-danger small">{{ errorFor(row, 'schoolId') }}</div>
                </template>
              </td>

              <!-- Status -->
              <td>
                <template v-if="row.index === editingIndex">
                  <span class="text-muted">editing…</span>
                </template>
                <template v-else>
                  <span v-if="row.errors.length === 0" class="text-success">✓</span>
                  <span v-else class="text-danger">🛑 {{ row.errors.length }} error(s)</span>
                </template>
              </td>

              <!-- Remove button -->
              <td>
                <button
                  v-if="row.index !== editingIndex"
                  class="btn btn-sm btn-outline-danger border-0"
                  title="Remove this row"
                  @click="emit('remove', row.index)"
                ><i class="fas fa-trash"></i></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Results Summary -->
    <div v-if="result">
      <h5>Import Complete</h5>
      <p>
        <strong>{{ result.created.length }}</strong> user<span v-if="result.created.length !== 1">s</span> created
        <span v-if="result.errors.length > 0" class="text-danger">
          · <strong>{{ result.errors.length }}</strong> error<span v-if="result.errors.length !== 1">s</span>
        </span>
      </p>
      <ul v-if="result.errors.length > 0" class="error-list">
        <li v-for="err in result.errors" :key="err.index" class="text-danger">
          Row {{ err.index + 1 }} ({{ err.email }}): {{ err.message }}
        </li>
      </ul>
    </div>

    <!-- Empty state -->
    <div v-if="!result && rows.length === 0 && !busy" class="text-muted text-center py-1">
      No rows yet — use the CSV input below or click <i class="fas fa-plus"></i> to add one
    </div>
  </div>
</template>

<style lang="scss" scoped>
.bulk-preview {
  margin: 1rem 0;
}

.text-danger.small {
  background-color: #fffde7;
  padding: 1px 4px;
  border-radius: 2px;
}

.error-list {
  padding-left: 1.2rem;
}
</style>
